// Shared helpers of the process page (/eventannouncement): step kinds, targets, event states and
// formatting. Mirrors AveOrga.Shared.Contracts.Processes — enums arrive as strings
// (JsonStringEnumConverter); numbers are accepted as a fallback.
import {
  NEWSLETTER_TARGET_LABEL, STEP_KIND_ICON, STEP_KIND_LABEL, TEMPLATE_RULES,
} from '../../util/processSteps';

// ── Step kinds ───────────────────────────────────────────────────────────────────────────────
export const STEP_KIND = {
  AssignLeaders: 'AssignLeaders',
  Release: 'Release',
  SendMail: 'SendMail',
  ManualCheck: 'ManualCheck',
};
const STEP_KIND_NAMES = ['AssignLeaders', 'Release', 'SendMail', 'ManualCheck'];

// null for the synthetic final step ("done").
export function normalizeStepKind(value) {
  if (value == null) return null;
  if (typeof value === 'number') return STEP_KIND_NAMES[value] ?? null;
  return STEP_KIND_NAMES.includes(value) ? value : null;
}

// Kind names and icons shared with the template editor (util/processSteps.js).
export { STEP_KIND_ICON, STEP_KIND_LABEL };

// Default label of a new step (editable).
export const STEP_KIND_DEFAULT_LABEL = STEP_KIND_LABEL;

export const FINAL_STEP_KEY = 'done';

// The synthetic final step has no stepId (and no kind).
export const isFinalStep = (step) => !!step && step.stepId == null;

export function stepIcon(step) {
  if (!step) return 'radio_button_unchecked';
  if (step.icon) return step.icon;
  if (isFinalStep(step)) return 'flag';
  return STEP_KIND_ICON[normalizeStepKind(step.kind)] ?? 'radio_button_unchecked';
}

// Step status strings from the API: done | skipped | pending | closed (final step only).
export const STEP_STATUS_LABEL = {
  done: 'Erledigt',
  skipped: 'Übersprungen',
  pending: 'Offen',
  closed: 'Abgeschlossen',
};

// Current step = first pending action step; the final step once everything is done/skipped or the
// process is closed.
export function currentStepKey(process) {
  const steps = process?.steps ?? [];
  const final = steps.find(isFinalStep)?.key ?? FINAL_STEP_KEY;
  if (!process || process.closed) return final;
  return steps.find(s => !isFinalStep(s) && s.status === 'pending')?.key ?? final;
}

// ── Step targets ─────────────────────────────────────────────────────────────────────────────
export const TARGET_KIND = { ParticipantGroup: 'ParticipantGroup', Newsletter: 'Newsletter' };
export const normalizeTargetKind = (value) =>
  (value === 'Newsletter' || value === 1 ? TARGET_KIND.Newsletter : TARGET_KIND.ParticipantGroup);

export const NEWSLETTER_TARGET_KEY = 'newsletter';
export const NEWSLETTER_LABEL = NEWSLETTER_TARGET_LABEL;

// Pseudo group for the newsletter chip (ParticipantGroupChip only needs name + colorKey).
export const NEWSLETTER_PSEUDO_GROUP = { id: -1, name: NEWSLETTER_LABEL, pluralName: NEWSLETTER_LABEL, colorKey: 'neutral' };

// A guest release means "public" — label used for release chips.
export const releaseLabel = (group) => (group?.isGuest ? 'Öffentlich' : (group?.pluralName || group?.name || ''));

// "Mitglieder, Newsletter" — targets of a step as text.
export function targetsSummary(step, groupsById) {
  return (step?.targets ?? []).map(t => {
    if (normalizeTargetKind(t.targetKind) === TARGET_KIND.Newsletter) return 'Newsletter';
    const group = groupsById?.get(Number(t.participantGroupId));
    if (normalizeStepKind(step.kind) === STEP_KIND.Release && group) return releaseLabel(group);
    return group?.pluralName || t.participantGroupName || '';
  }).filter(Boolean).join(', ');
}

// Group ids released by the release steps of a process (header chips "Freigabe für").
export function releaseTargetGroupIds(process) {
  const ids = new Set();
  (process?.steps ?? [])
    .filter(s => normalizeStepKind(s.kind) === STEP_KIND.Release)
    .forEach(s => (s.targets ?? []).forEach(t => {
      if (t.participantGroupId != null) ids.add(Number(t.participantGroupId));
    }));
  return ids;
}

// Rules of a template / process (same flags on ProcessTemplateResponse and EventProcessResponse) as
// short badges with explanation (tooltip) — wording as on the template cards (/processtemplates).
const PROCESS_RULE_KEYS = new Set(['neverPublic', 'linkSignup', 'prepaymentRequired', 'requiresLeader']);

export function ruleBadges(rules) {
  if (!rules) return [];
  return TEMPLATE_RULES
    .filter(r => PROCESS_RULE_KEYS.has(r.key) && rules[r.key])
    .map(r => ({ key: r.key, icon: r.icon, label: r.badge, hint: r.hint }));
}

// ── Events ───────────────────────────────────────────────────────────────────────────────────
// EventState: Draft=0, Published=1, Cancelled=2, Completed=3, MemberPreview=4.
export const isCancelledState = (state) => state === 'Cancelled' || state === 2;
export const isCompletedState = (state) => state === 'Completed' || state === 3;

// "Open" = still changeable (not cancelled/completed) — the events a step acts on.
export const isOpenEvent = (evt) => !!evt && !isCancelledState(evt.state) && !isCompletedState(evt.state);

// Wording as before: link sign-up processes hold "Events", the others "Spieltage".
export function eventNouns(process) {
  const label = process?.eventLabel;
  const isGame = label ? label === 'Spieltag' : !process?.linkSignup;
  return isGame ? { one: 'Spieltag', many: 'Spieltage' } : { one: 'Event', many: 'Events' };
}

// ── Formatting ───────────────────────────────────────────────────────────────────────────────
export function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

export const formatTime = (t) => (t ? t.substring(0, 5) : '');

export function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

// "12.04.2026 · 10:00 · Feld A"
export function eventShortLabel(evt) {
  if (!evt) return '';
  return [formatDate(evt.date), formatTime(evt.time), evt.locationName].filter(Boolean).join(' · ');
}

// API error text for display: only strings/titles — a JSON body (e.g. 500 `{ error }` or
// ValidationProblemDetails) must never be rendered as a React child.
export function apiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.title) return data.title;
  if (!err?.response) return 'Keine Verbindung – bitte später erneut versuchen.';
  return fallback;
}

// Idempotency key of one "Senden" click (max. 36 chars). crypto.randomUUID only exists in secure
// contexts — on plain http (LAN) fall back to getRandomValues (v4 layout).
export function newExecutionId() {
  const c = typeof window !== 'undefined' ? window.crypto : null;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// Integer input: empty -> fallback, otherwise an integer in [min, max] or null (invalid -> message
// instead of silently replacing it). badInput: the browser reports value === '' for unparsable
// input (e.g. "2e") — that is not "empty".
export function parseIntInput(input, min, max, fallback) {
  if (input?.validity?.badInput) return null;
  const raw = String(input?.value ?? '').trim();
  if (raw === '') return fallback;
  const n = Number(raw);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
}

// EventProcessResponse → EventProcessSummary (keeps the selection list in sync without GetAll).
export function summaryFromDetail(detail, previous = {}) {
  return {
    ...previous,
    id: detail.id,
    displayName: detail.displayName,
    createdAt: detail.createdAt ?? previous.createdAt,
    closed: detail.closed,
    eventCount: detail.events?.length ?? previous.eventCount ?? 0,
    currentStep: detail.currentStep,
    processTemplateId: detail.processTemplateId ?? previous.processTemplateId,
    processTemplateName: detail.processTemplateName ?? previous.processTemplateName,
    eventLabel: detail.eventLabel ?? previous.eventLabel,
    locationId: detail.locationId ?? previous.locationId,
    locationName: detail.locationName ?? previous.locationName ?? null,
  };
}

// "Bad Wildbad" — location of a process (every process has one); null without a process.
export const processLocationName = (process) =>
  (process ? (process.locationName || 'Ort des Prozesses') : null);
